import * as React from 'react';
import { Box, Chip, Grid, MenuItem, Switch, TextField, Typography } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { Field } from 'react-final-form';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import { FormSectionCard } from '../../components/cards/FormSectionCard';
import { FirmTeamSelector } from '../../lookupConfiguration/selectors/FirmTeamSelector';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import {
    TASKFLOW_START_STATUS_OPTIONS,
    TASKFLOW_TASK_TYPE_OPTIONS,
    TaskflowTaskPriority,
} from '../types';
import { ResponsibilitySelector } from '../selectors/ResponsibilitySelector';
import { ConfigureTabProps } from './ConfigureTabProps';

const BLUE = '#1565c0';

const PRIORITY_OPTIONS: { value: TaskflowTaskPriority; label: string }[] = [
    { value: 'low', label: 'Low' },
    { value: 'med', label: 'Medium' },
    { value: 'high', label: 'High' },
];

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        fieldRow: {
            marginBottom: theme.spacing(2.5),
        },
        label: {
            fontSize: 11.5,
            fontWeight: 700,
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: theme.spacing(0.75),
        },
        help: {
            fontSize: 11.5,
            color: theme.palette.text.disabled,
            marginTop: theme.spacing(0.75),
        },
        delegateToggleCard: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1.5),
            backgroundColor: '#eef1fb',
            borderRadius: 10,
            padding: theme.spacing(1.25, 1.5),
            marginBottom: theme.spacing(1.5),
        },
        delegateToggleTitleRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        delegateToggleTitle: {
            fontSize: 13.5,
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
        delegateNewChip: {
            height: 18,
            fontSize: 10,
            fontWeight: 700,
            color: '#fff',
            backgroundColor: '#2e7d32',
        },
        delegateToggleDescription: {
            fontSize: 12,
            color: theme.palette.text.secondary,
            marginTop: theme.spacing(0.5),
        },
        priorityRow: {
            display: 'flex',
            gap: theme.spacing(1),
        },
        priorityOpt: {
            flex: 1,
            textAlign: 'center',
            padding: theme.spacing(1, 0),
            borderRadius: 8,
            border: `1.5px solid ${theme.palette.divider}`,
            fontSize: 12,
            fontWeight: 700,
            color: theme.palette.text.secondary,
            cursor: 'pointer',
        },
    })
);

const PRIORITY_SELECTED_STYLE: Record<TaskflowTaskPriority, React.CSSProperties> = {
    low: { backgroundColor: 'rgba(21, 101, 192, 0.08)', borderColor: '#1565c0', color: '#1565c0' },
    med: { backgroundColor: 'rgba(225, 135, 19, 0.14)', borderColor: '#e18713', color: '#a15f04' },
    high: { backgroundColor: 'rgba(213, 0, 0, 0.08)', borderColor: '#d50000', color: '#d50000' },
};

export const DetailsTab: React.FC<ConfigureTabProps> = ({ form, values, taskflow }) => {
    const classes = useStyles();

    return (
        <FormSectionCard
            title="Task Details"
            description="What this task is, how it's actioned, and who it's assigned to."
            color={BLUE}
            bgColor="#e3f2fd"
            icon={AssignmentOutlinedIcon}
        >
            <Box className={classes.fieldRow}>
                <Typography className={classes.label}>Task name</Typography>
                <Field name="title" subscription={{ value: true }}>
                    {({ input }) => (
                        <TextField {...input} fullWidth={true} variant="outlined" size="small" placeholder="e.g. Report to client" />
                    )}
                </Field>
            </Box>

            <Box className={classes.fieldRow}>
                <Typography className={classes.label}>Description</Typography>
                <Field name="description" subscription={{ value: true }}>
                    {({ input }) => (
                        <TextField
                            {...input}
                            fullWidth={true}
                            multiline={true}
                            minRows={2}
                            variant="outlined"
                            size="small"
                        />
                    )}
                </Field>
            </Box>

            <Box className={classes.fieldRow}>
                <Typography className={classes.label}>Template (optional)</Typography>
                <Field name="template" subscription={{ value: true }}>
                    {({ input }) => (
                        <TextField {...input} select={true} fullWidth={true} variant="outlined" size="small">
                            {taskflow.templateOptions.map(option => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </TextField>
                    )}
                </Field>
                <Typography className={classes.help}>
                    What this task sends when it's actioned — leave as "None" for internal-only tasks like approvals.
                </Typography>
            </Box>

            <Grid container={true} spacing={2}>
                <Grid item={true} xs={6}>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.label}>Starting status</Typography>
                        <Field name="startStatus" subscription={{ value: true }}>
                            {({ input }) => (
                                <TextField {...input} select={true} fullWidth={true} variant="outlined" size="small">
                                    {TASKFLOW_START_STATUS_OPTIONS.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </TextField>
                            )}
                        </Field>
                    </Box>
                </Grid>
                <Grid item={true} xs={6}>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.label}>Task type</Typography>
                        <Field name="taskType" subscription={{ value: true }}>
                            {({ input }) => (
                                <TextField {...input} select={true} fullWidth={true} variant="outlined" size="small">
                                    {TASKFLOW_TASK_TYPE_OPTIONS.map(option => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </TextField>
                            )}
                        </Field>
                    </Box>
                </Grid>
            </Grid>

            <Box className={classes.fieldRow}>
                <Typography className={classes.label}>Assign to</Typography>
                <Field name="assignMode" subscription={{ value: true }}>
                    {({ input }) => (
                        <ToggleButtonGroup
                            exclusive={true}
                            size="small"
                            value={input.value}
                            onChange={(event: React.MouseEvent<HTMLElement>, nextMode: 'resp' | 'team' | null) => {
                                if (!nextMode) {
                                    return;
                                }
                                input.onChange(nextMode);
                                form.change('assignValue', undefined);
                            }}
                            style={{ marginBottom: 8 }}
                        >
                            <ToggleButton value="resp">Responsibility</ToggleButton>
                            <ToggleButton value="team">Team</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                </Field>
                <Field name="assignValue" subscription={{ value: true }}>
                    {({ input }) => (
                        values.assignMode === 'resp' ? (
                            <ResponsibilitySelector
                                name={input.name}
                                value={input.value}
                                onSelection={(nextValue: IAutoCompleteItem | IAutoCompleteItem[]) => input.onChange(nextValue)}
                                variant="outlined"
                                size="small"
                                disablePortal={true}
                            />
                        ) : (
                            <FirmTeamSelector
                                name={input.name}
                                value={input.value}
                                onSelection={(nextValue: IAutoCompleteItem | IAutoCompleteItem[]) => input.onChange(nextValue)}
                                isMyTeamOnly={null}
                                enquiryOnly={false}
                                financialOnly={false}
                                taskOnly={true}
                                variant="outlined"
                                size="small"
                                disablePortal={true}
                            />
                        )
                    )}
                </Field>
                <Typography className={classes.help}>Resolved from Inprotech data on the matter when the task is created.</Typography>
            </Box>

            <Box className={classes.fieldRow}>
                <Box className={classes.delegateToggleCard}>
                    <Box>
                        <Box className={classes.delegateToggleTitleRow}>
                            <Typography className={classes.delegateToggleTitle}>Allow delegate to</Typography>
                            <Chip label="NEW" size="small" className={classes.delegateNewChip} />
                        </Box>
                        <Typography className={classes.delegateToggleDescription}>
                            Lets whoever's assigned hand this task to someone else without losing ownership — the
                            "&rarr; delegate" action on My Tasks. Off by default; most steps don't call for it.
                        </Typography>
                    </Box>
                    <Field name="allowDelegate" subscription={{ value: true }}>
                        {({ input }) => (
                            <Switch
                                checked={!!input.value}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => input.onChange(event.target.checked)}
                                color="primary"
                            />
                        )}
                    </Field>
                </Box>

                {values.allowDelegate && (
                    <React.Fragment>
                        <Typography className={classes.label}>Delegate to</Typography>
                        <Field name="delegateMode" subscription={{ value: true }}>
                            {({ input }) => (
                                <ToggleButtonGroup
                                    exclusive={true}
                                    size="small"
                                    value={input.value}
                                    onChange={(event: React.MouseEvent<HTMLElement>, nextMode: 'resp' | 'team' | null) => {
                                        if (!nextMode) {
                                            return;
                                        }
                                        input.onChange(nextMode);
                                        form.change('delegateValue', undefined);
                                    }}
                                    style={{ marginBottom: 8 }}
                                >
                                    <ToggleButton value="resp">Responsibility</ToggleButton>
                                    <ToggleButton value="team">Team</ToggleButton>
                                </ToggleButtonGroup>
                            )}
                        </Field>
                        <Field name="delegateValue" subscription={{ value: true }}>
                            {({ input }) => (
                                values.delegateMode === 'resp' ? (
                                    <ResponsibilitySelector
                                        name={input.name}
                                        value={input.value}
                                        onSelection={(nextValue: IAutoCompleteItem | IAutoCompleteItem[]) => input.onChange(nextValue)}
                                        variant="outlined"
                                        size="small"
                                        disablePortal={true}
                                    />
                                ) : (
                                    <FirmTeamSelector
                                        name={input.name}
                                        value={input.value}
                                        onSelection={(nextValue: IAutoCompleteItem | IAutoCompleteItem[]) => input.onChange(nextValue)}
                                        isMyTeamOnly={null}
                                        enquiryOnly={false}
                                        financialOnly={false}
                                        taskOnly={true}
                                        variant="outlined"
                                        size="small"
                                        disablePortal={true}
                                    />
                                )
                            )}
                        </Field>
                        <Typography className={classes.help}>
                            Same responsibility/team picker as Assign to, above — resolved the same way when the
                            task is created.
                        </Typography>
                    </React.Fragment>
                )}
            </Box>

            <Grid container={true} spacing={2}>
                <Grid item={true} xs={6}>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.label}>Priority</Typography>
                        <Field name="priority" subscription={{ value: true }}>
                            {({ input }) => (
                                <Box className={classes.priorityRow}>
                                    {PRIORITY_OPTIONS.map(option => (
                                        <Box
                                            key={option.value}
                                            className={classes.priorityOpt}
                                            style={input.value === option.value ? PRIORITY_SELECTED_STYLE[option.value] : undefined}
                                            onClick={() => input.onChange(option.value)}
                                        >
                                            {option.label}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Field>
                    </Box>
                </Grid>
                <Grid item={true} xs={6}>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.label}>Estimated effort</Typography>
                        <Field name="effort" subscription={{ value: true }}>
                            {({ input }) => (
                                <TextField {...input} fullWidth={true} variant="outlined" size="small" placeholder="00:00" />
                            )}
                        </Field>
                    </Box>
                </Grid>
            </Grid>
        </FormSectionCard>
    );
};
